import React, { useState, useEffect } from "react";

const GoalBasedInvestmentCalculator = () => {
  const [goalAmount, setGoalAmount] = useState(2000000);
  const [timeHorizon, setTimeHorizon] = useState(10);
  const [currentSavings, setCurrentSavings] = useState(100000);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [inflationRate, setInflationRate] = useState(6);
  const [investmentFrequency, setInvestmentFrequency] = useState("monthly");
  const [result, setResult] = useState({
    inflationAdjustedGoal: "0.00",
    requiredInvestment: "0.00",
    totalInvestment: "0.00",
    currentSavingsGrowth: "0.00",
    shortfall: "0.00",
    achievementPercentage: "0",
  });

  // Goal Based Investment Calculation Function
  const calculateGoal = () => {
    const goal = parseFloat(goalAmount);
    const years = parseFloat(timeHorizon);
    const current = parseFloat(currentSavings);
    const returnRate = parseFloat(expectedReturn) / 100;
    const inflation = parseFloat(inflationRate) / 100;

    if (isNaN(goal) || isNaN(years) || isNaN(current) || isNaN(returnRate) || 
        goal <= 0 || years <= 0 || current < 0) return;

    // Calculate inflation-adjusted goal amount
    const inflationAdjustedGoal = goal * Math.pow(1 + inflation, years);

    // Calculate future value of current savings
    const currentSavingsGrowth = current * Math.pow(1 + returnRate, years);

    // Calculate shortfall after current savings growth
    const shortfall = Math.max(0, inflationAdjustedGoal - currentSavingsGrowth);

    // Calculate required periodic investment based on frequency
    let periodsPerYear, periodicRate, totalPeriods;
    
    if (investmentFrequency === "monthly") {
      periodsPerYear = 12;
      periodicRate = returnRate / 12;
      totalPeriods = years * 12;
    } else if (investmentFrequency === "quarterly") {
      periodsPerYear = 4;
      periodicRate = returnRate / 4;
      totalPeriods = years * 4;
    } else { // yearly
      periodsPerYear = 1;
      periodicRate = returnRate;
      totalPeriods = years;
    }

    // Using Future Value of Annuity formula: FV = P × [((1 + r)^n - 1) / r] × (1 + r)
    // Solving for P: P = FV / ([((1 + r)^n - 1) / r] × (1 + r))
    let requiredInvestment = 0;
    if (shortfall > 0) {
      if (periodicRate > 0) {
        requiredInvestment = shortfall / 
          ((Math.pow(1 + periodicRate, totalPeriods) - 1) / periodicRate * (1 + periodicRate));
      } else {
        requiredInvestment = shortfall / totalPeriods;
      }
    }

    const totalInvestment = (requiredInvestment * totalPeriods) + current;

    // Calculate achievement percentage
    const totalCorpus = currentSavingsGrowth + (requiredInvestment > 0 ? shortfall : 0);
    const achievementPercentage = Math.min(100, (totalCorpus / inflationAdjustedGoal) * 100);

    setResult({
      inflationAdjustedGoal: inflationAdjustedGoal.toFixed(2),
      requiredInvestment: requiredInvestment.toFixed(2),
      totalInvestment: totalInvestment.toFixed(2),
      currentSavingsGrowth: currentSavingsGrowth.toFixed(2),
      shortfall: shortfall.toFixed(2),
      achievementPercentage: achievementPercentage.toFixed(1),
    });
  };

  // Run default calculation on first load
  useEffect(() => {
    calculateGoal();
  }, []);

  const frequencyLabel = investmentFrequency === "monthly" ? "Monthly" : 
                        investmentFrequency === "quarterly" ? "Quarterly" : "Yearly";

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
          </div>
          <h1>Goal Based Investment Calculator</h1>
          <p>
            Plan your financial goals and calculate how much to invest regularly to achieve them
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                  <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                  <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                </svg>
                Goal Details
              </h2>

              {/* Goal Amount */}
              <div className="input-group">
                <label>Target Goal Amount (Today's Value)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={goalAmount}
                    onChange={(e) => setGoalAmount(e.target.value)}
                    className="calc-input"
                    placeholder="2000000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 2000000, 5000000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setGoalAmount(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{(amount / 100000).toFixed(0)}L
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Horizon */}
              <div className="input-group">
                <label>Time Horizon (years)</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={timeHorizon}
                    onChange={(e) => setTimeHorizon(e.target.value)}
                    className="calc-input"
                    placeholder="10"
                  />
                </div>
                <div className="quick-select-group">
                  {[5, 10, 15, 20].map((years) => (
                    <button
                      key={years}
                      onClick={() => setTimeHorizon(years)}
                      className="quick-select-btn success"
                    >
                      {years}Y
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Savings */}
              <div className="input-group">
                <label>Current Savings (if any)</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={currentSavings}
                    onChange={(e) => setCurrentSavings(e.target.value)}
                    className="calc-input"
                    placeholder="100000"
                  />
                </div>
                <div className="quick-select-group">
                  {[0, 50000, 100000, 500000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setCurrentSavings(amount)}
                      className="quick-select-btn secondary"
                    >
                      {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Expected Return */}
              <div className="input-group">
                <label>Expected Annual Return</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={expectedReturn}
                    onChange={(e) => setExpectedReturn(e.target.value)}
                    className="calc-input"
                    placeholder="12"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
                <div className="quick-select-group">
                  {[8, 10, 12, 15].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => setExpectedReturn(rate)}
                      className="quick-select-btn secondary"
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Inflation Rate */}
              <div className="input-group">
                <label>Expected Inflation Rate</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(e.target.value)}
                    className="calc-input"
                    placeholder="6"
                    step="0.1"
                  />
                  <span className="input-suffix">%</span>
                </div>
              </div>

              {/* Investment Frequency */}
              <div className="input-group">
                <label>Investment Frequency</label>
                <div className="quick-select-group">
                  {[
                    { value: "monthly", label: "Monthly" },
                    { value: "quarterly", label: "Quarterly" },
                    { value: "yearly", label: "Yearly" }
                  ].map((freq) => (
                    <button
                      key={freq.value}
                      onClick={() => setInvestmentFrequency(freq.value)}
                      className={`quick-select-btn ${investmentFrequency === freq.value ? 'primary' : 'secondary'}`}
                    >
                      {freq.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateGoal} className="calculate-btn">
                Calculate Investment Plan
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Investment Plan
              </h2>

              <div className="result-card">
                {/* Inflation Adjusted Goal - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Inflation Adjusted Goal</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <circle cx="12" cy="12" r="6"></circle>
                        <circle cx="12" cy="12" r="2"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.inflationAdjustedGoal).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After {timeHorizon} years at {inflationRate}% inflation
                  </div>
                </div>

                {/* Required Investment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">{frequencyLabel} Investment Required</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.requiredInvestment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext green">
                    Starting today for {timeHorizon} years
                  </div>
                </div>

                {/* Current Savings Growth */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Current Savings Will Grow To</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.currentSavingsGrowth).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    At {expectedReturn}% annual return
                  </div>
                </div>

                {/* Shortfall */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Shortfall to Cover</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.shortfall).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Amount needed beyond current savings
                  </div>
                </div>

                {/* Total Investment */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Investment Required</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"></path>
                        <path d="M4 6v12c0 1.1.9 2 2 2h14v-4"></path>
                        <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalInvestment).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Including current savings
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Goal Achievement</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Current Savings Contribution</span>
                      <span className="breakdown-percentage">
                        {((Number(result.currentSavingsGrowth) / Number(result.inflationAdjustedGoal)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${Math.min(100, (Number(result.currentSavingsGrowth) / Number(result.inflationAdjustedGoal)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Future Investments Needed</span>
                      <span className="breakdown-percentage green">
                        {((Number(result.shortfall) / Number(result.inflationAdjustedGoal)) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${Math.min(100, (Number(result.shortfall) / Number(result.inflationAdjustedGoal)) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>How Does the Goal Based Investment Calculator Work?</h3>
              <p>
                The Goal Based Investment Calculator helps you plan for specific financial goals like 
                buying a house, child's education, or dream vacation. It adjusts your goal for inflation, 
                factors in your existing savings and their growth, calculates the shortfall, and tells 
                you exactly how much to invest periodically (monthly, quarterly, or yearly) to achieve 
                your goal. This systematic approach ensures you stay on track with your financial dreams.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Key Formulas Used:</h4>
            <div className="formula-display">
              P = FV / ([((1 + r)^n - 1) / r] × (1 + r))
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">P</div>
                <div className="formula-param-value">Periodic Investment Required</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">FV</div>
                <div className="formula-param-value">Future Value (Shortfall Amount)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">r</div>
                <div className="formula-param-value">Periodic Return Rate</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">n</div>
                <div className="formula-param-value">Number of Periods</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>What is goal-based investing?</h5>
              <p>
                Goal-based investing is a strategy where you align your investments with specific life 
                goals like buying a house, child's education, retirement, or vacation. Instead of 
                investing randomly, you calculate exactly how much you need, when you need it, and 
                invest accordingly. This approach provides clarity, discipline, and increases the 
                likelihood of achieving your financial dreams. Each goal has different time horizons 
                and risk profiles, requiring tailored investment strategies.
              </p>
            </div>

            <div className="faq-item">
              <h5>How should I allocate assets for different goal timelines?</h5>
              <p>
                For short-term goals (1-3 years), use debt funds, liquid funds, or FDs for capital 
                protection. For medium-term goals (3-5 years), use balanced or hybrid funds with 40-60% 
                equity exposure. For long-term goals (5+ years), aggressive equity allocation (70-80%) 
                maximizes growth potential. As you near your goal date, gradually shift to safer 
                instruments. For example, if saving for a house in 10 years, start with 80% equity, 
                then reduce to 50% after 7 years, and 20% in the final 2 years.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I account for inflation in my goals?</h5>
              <p>
                Absolutely essential. Inflation erodes purchasing power significantly over time. A house 
                costing ₹50 lakh today will cost ₹90 lakh in 10 years at 6% inflation. Education costs 
                inflate even faster at 10-12% annually. Always calculate your goals in future value 
                terms. This calculator automatically adjusts your target amount for inflation, ensuring 
                your investments will actually meet your needs when the time comes. Never plan in 
                today's money for future goals.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if I already have some savings towards my goal?</h5>
              <p>
                Existing savings give you a head start and reduce future investment requirements. This 
                calculator factors in your current savings and projects their growth at your expected 
                return rate. It then calculates only the shortfall you need to cover through periodic 
                investments. Even small existing savings compound significantly over time. For example, 
                ₹1 lakh today growing at 12% for 10 years becomes ₹3.1 lakh, substantially reducing 
                your monthly investment burden.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I choose between monthly, quarterly, or yearly investments?</h5>
              <p>
                Monthly investments (SIPs) are ideal for salaried individuals as they align with income 
                cycles and provide rupee cost averaging benefits. They're also easier on cash flow. 
                Quarterly investments suit those with irregular income or quarterly bonuses. Yearly 
                investments work for business owners or those receiving annual bonuses. More frequent 
                investments generally result in slightly better compounding due to earlier money entry, 
                but the difference is marginal. Choose what fits your income pattern and discipline.
              </p>
            </div>

            <div className="faq-item">
              <h5>What should I do if I'm falling short of my goal?</h5>
              <p>
                If you're not on track, you have several options: increase your periodic investment 
                amount, extend your goal timeline if possible, increase expected returns by taking 
                slightly higher risk (within limits), reduce the goal amount, or add lump sum 
                investments when you receive bonuses. Review your goals annually and adjust. Sometimes 
                cutting unnecessary expenses and redirecting them to investments makes a huge difference. 
                The earlier you identify and correct shortfalls, the easier it is to get back on track.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalBasedInvestmentCalculator;